/**
 * Flow tag rule formatting + option lists for the editor & list rows.
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  FlowTagCondition,
  FlowTagRankingMetric,
  FlowTagRankingOp,
  FlowTagRule,
} from '@trafficpulse/shared';
import { getStatsDateRangeLabel } from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';
import { formatDateDMY } from '@/features/smswiser-campaigns/utils/dateFormat';
import { LIST_METRIC_FIELDS, OPERATORS, UNGROUPED_KEY } from '@/pages/admin/ListTagRulesPage/constants';

export const RANKING_METRIC_OPTIONS: {
  key: FlowTagRankingMetric;
  label: string;
  hint: string;
}[] = [
  {
    key: 'rawScore',
    label: 'Raw score',
    hint: 'Sorts by the raw number your formula gives each item — higher means it performed better.',
  },
  {
    key: 'scoreShare',
    label: 'Score share',
    hint: "Sorts by each item's slice of the group's total score (the slices add up to 100%). Same order as Raw score, just shown as a %.",
  },
  {
    key: 'optimalWeight',
    label: 'Optimal weight',
    hint: "Sorts by the new traffic % the optimizer actually gives each item (the “New” value, after its limits) — i.e. who really gets the most traffic.",
  },
];

export const RANKING_OP_OPTIONS: { op: FlowTagRankingOp; label: string; needsPercent: boolean }[] = [
  { op: 'highest', label: 'Is highest', needsPercent: false },
  { op: 'lowest', label: 'Is lowest', needsPercent: false },
  { op: 'top_percent', label: 'In top %', needsPercent: true },
  { op: 'bottom_percent', label: 'In bottom %', needsPercent: true },
];

export const RANKING_SCOPE_OPTIONS = [
  { value: 'path' as const, label: 'Within path' },
  { value: 'flow' as const, label: 'Across flow' },
];

const rankingMetricLabel = (m: FlowTagRankingMetric) =>
  RANKING_METRIC_OPTIONS.find((o) => o.key === m)?.label ?? m;

function formatRanking(c: Extract<FlowTagCondition, { kind: 'ranking' }>): string {
  const metric = rankingMetricLabel(c.metric);
  const scope = c.scope === 'flow' ? ' across flow' : ' within path';
  if (c.op === 'highest') return `${metric}: highest${scope}`;
  if (c.op === 'lowest') return `${metric}: lowest${scope}`;
  const side = c.op === 'top_percent' ? 'top' : 'bottom';
  return `${metric}: ${side} ${c.percent ?? 0}%${scope}`;
}

const fieldLabel = (key: string): string =>
  LIST_METRIC_FIELDS.find((f) => f.key === key)?.label ?? key;

// Overrides render as an OverridesBadge pill on the row, not inline — a dozen
// `country:value` pairs in parentheses buried the threshold they modify.
function formatMetric(c: Extract<FlowTagCondition, { kind: 'metric' }>): string {
  const field = fieldLabel(c.field);
  const op = OPERATORS.find((o) => o.op === c.operator)?.label ?? c.operator;
  const ct = c.compareTo;
  if (ct) {
    const win = ct.dateRangePreset
      ? getStatsDateRangeLabel(ct.dateRangePreset)
      : 'rule window';
    const pct = ct.percent === undefined ? '' : `${ct.percent}% of `;
    const src = ct.against === 'total' ? 'TOTAL ' : '';
    return `${field} ${op} ${pct}${src}${fieldLabel(ct.field)} (${win})`;
  }
  return `${field} ${op} ${c.value}`;
}

export function formatFlowCondition(c: FlowTagCondition): string {
  return c.kind === 'ranking' ? formatRanking(c) : formatMetric(c);
}

export function getFlowRuleDateLabel(rule: FlowTagRule): string {
  if (rule.dateRangePreset === 'custom' && rule.fromDate) {
    if (rule.toDateIsToday) return `${formatDateDMY(rule.fromDate)} → Today`;
    if (rule.toDate) return `${formatDateDMY(rule.fromDate)} → ${formatDateDMY(rule.toDate)}`;
  }
  return getStatsDateRangeLabel(rule.dateRangePreset ?? 'last-30-days');
}

export function groupFlowRules(
  rules: FlowTagRule[],
): Array<{ key: string; label: string; rules: FlowTagRule[] }> {
  const byGroup = new Map<string, FlowTagRule[]>();
  for (const r of rules) {
    const key = (r.groupKey ?? '').trim() || UNGROUPED_KEY;
    const list = byGroup.get(key) ?? [];
    list.push(r);
    byGroup.set(key, list);
  }
  for (const list of byGroup.values()) {
    list.sort((a, b) => (b.orderIndex ?? 0) - (a.orderIndex ?? 0));
  }
  const ordered: Array<{ key: string; label: string; rules: FlowTagRule[] }> = [];
  const ungrouped = byGroup.get(UNGROUPED_KEY);
  if (ungrouped?.length) ordered.push({ key: UNGROUPED_KEY, label: 'No group', rules: ungrouped });
  const rest = [...byGroup.entries()].filter(([k]) => k !== UNGROUPED_KEY);
  rest.sort((a, b) => a[0].localeCompare(b[0]));
  for (const [key, list] of rest) ordered.push({ key, label: key, rules: list });
  return ordered;
}
